import { useState, useEffect, useCallback } from 'react';
import type { CurrentPrice, DayPricesResponse, ApiError } from '@/types';
import * as api from '@/lib/api';

interface UsePricesResult<T> {
  data: T | null;
  loading: boolean;
  error: ApiError | null;
  refetch: () => void;
}

export function useCurrentPrices(): UsePricesResult<CurrentPrice[]> {
  const [data, setData] = useState<CurrentPrice[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchData = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.getCurrentPrices(signal);
      setData(result);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return; // Ignore aborted requests
      }
      setError(err as ApiError);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    fetchData(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [fetchData]);

  return { data, loading, error, refetch: () => fetchData() };
}

export function useDayPrices(
  area: string,
  dateOrType: string // 'latest', 'tomorrow', or 'YYYY-MM-DD'
): UsePricesResult<DayPricesResponse> {
  const [data, setData] = useState<DayPricesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchData = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      let result: DayPricesResponse;
      if (dateOrType === 'latest') {
        result = await api.getLatestPrices(area, signal);
      } else if (dateOrType === 'tomorrow') {
        result = await api.getTomorrowPrices(area, signal);
      } else {
        result = await api.getPricesByDate(area, dateOrType, signal);
      }
      setData(result);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return; // Ignore aborted requests
      }
      setError(err as ApiError);
    } finally {
      setLoading(false);
    }
  }, [area, dateOrType]);

  useEffect(() => {
    const abortController = new AbortController();
    fetchData(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [fetchData]);

  return { data, loading, error, refetch: () => fetchData() };
}
